import { ref, computed } from "vue";
import { defineStore } from "pinia";

// 广告位 ID 常量 (根据你的实际情况修改或添加)
const INTERSTITIAL_PLACEMENT_ID = '5146515282268289';
const RETRY_DELAY_MS = 5000; // 加载失败后的重试延迟

export const useAdStore = defineStore("ads", () => {
  // 存储广告队列，以 placementId 为 key
  // adQueue: { 'placementId1': [adData1, adData2], 'placementId2': [adData3] }
  const adQueue = ref({});
  // 存储加载状态，以 placementId 为 key
  const isLoading = ref({});
  // 新增：标记 GDT SDK 是否已基本就绪 (第一次 onComplete 回调后)
  const sdkReady = ref(false);

  // --- Getters (Computed) ---

  /**
   * 获取指定广告位下一个可用的广告数据（不消耗）
   * @param {string} placementId - 广告位 ID
   * @returns {object | null} - 广告数据对象或 null
   */
  const getNextAd = (placementId) => {
    return adQueue.value[placementId]?.[0] || null;
  };

  /**
   * 检查指定广告位是否有可用的广告
   * @param {string} placementId - 广告位 ID
   * @returns {boolean}
   */
  const hasAds = (placementId) => {
    return (adQueue.value[placementId]?.length || 0) > 0;
  };

  /**
   * 检查指定广告位当前是否正在加载
   * @param {string} placementId - 广告位 ID
   * @returns {boolean}
   */
  const checkIsLoading = (placementId) => {
    return isLoading.value[placementId] || false;
  };


  // --- Actions ---

  /**
   * 新增：设置 SDK 已就绪状态
   */
  const setSdkReady = () => {
      if (!sdkReady.value) {
          console.log("[AdStore] GDT SDK is now considered ready.");
          sdkReady.value = true;
      }
  };

  /**
   * 将加载到的广告数据添加到队列
   * @param {string} placementId - 广告位 ID
   * @param {Array} ads - 从 onComplete 返回的广告数据数组
   */
  const addAds = (placementId, ads) => {
    if (!adQueue.value[placementId]) {
      adQueue.value[placementId] = [];
    }
    adQueue.value[placementId].push(...ads);
    isLoading.value[placementId] = false;
    console.log(`[AdStore] Added ${ads.length} ads for ${placementId}. Queue size: ${adQueue.value[placementId].length}`);
  };

  /**
   * 消耗（移除）一个已渲染的广告
   * @param {string} placementId - 广告位 ID
   */
  const consumeAd = (placementId) => {
    if (adQueue.value[placementId]?.length > 0) {
      adQueue.value[placementId].shift();
      console.log(`[AdStore] Consumed ad for ${placementId}. Remaining: ${adQueue.value[placementId].length}`);
    }
  };

  /**
   * 处理广告加载失败
   * @param {string} placementId - 广告位 ID
   * @param {object} errorRes - 从 onComplete 返回的错误信息
   */
  const handleLoadFailure = (placementId, errorRes) => {
    console.error(`[AdStore] Failed to load ads for ${placementId}:`, errorRes);
    isLoading.value[placementId] = false;
    // 可选：在这里实现失败重试逻辑，例如延迟一段时间后再次调用 loadAds
    // console.log(`[AdStore] Retrying load for ${placementId} in ${RETRY_DELAY_MS}ms`);
    // setTimeout(() => {
    //   loadAds(placementId);
    // }, RETRY_DELAY_MS);
  };


  /**
   * 触发加载指定广告位的广告
   * @param {string} placementId - 广告位 ID
   */
  const loadAds = (placementId) => {
    // 在调用前检查 SDK 是否就绪
    if (!sdkReady.value) {
      console.warn(`[AdStore] Attempted to load ads for ${placementId}, but SDK is not ready yet. Please wait for the initial load to complete.`);
      // 不设置 isLoading，因为没有真正开始加载
      return;
    }

    if (isLoading.value[placementId]) {
      console.log(`[AdStore] Ad request for ${placementId} already in progress.`);
      return;
    }

    console.log(`[AdStore] Requesting ads for ${placementId}...`);
    isLoading.value[placementId] = true;

    try {
      // 检查 TencentGDT 和 NATIVE 是否可用
      if (window.TencentGDT && window.TencentGDT.NATIVE && typeof window.TencentGDT.NATIVE.loadAd === 'function') {
        window.TencentGDT.NATIVE.loadAd(placementId);
      } else {
        console.error(`[AdStore] TencentGDT.NATIVE.loadAd is not available (SDK might not be fully initialized).`);
        isLoading.value[placementId] = false;
        // 触发失败处理，因为无法调用 loadAd
        handleLoadFailure(placementId, { ret: -998, msg: 'SDK loadAd function not available when called' });
      }
    } catch (error) {
      console.error(`[AdStore] Error calling loadAd for ${placementId}:`, error);
      isLoading.value[placementId] = false;
      handleLoadFailure(placementId, { ret: -999, msg: 'Error calling SDK loadAd', error });
    }
  };

  return {
    sdkReady, // 暴露状态 (只读)

    // Getters/Computed
    getNextAd,
    hasAds,
    checkIsLoading,

    // Actions
    setSdkReady, // 新增 Action
    addAds,
    consumeAd,
    loadAds,
    handleLoadFailure,

    // 常量 (如果需要在组件中使用)
    INTERSTITIAL_PLACEMENT_ID,
  };
}); 